import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Maximize2, Minimize2, ShieldAlert, X } from 'lucide-react';
import DialogShell from './kit/DialogShell';
import { api } from './api';
import { toast } from '../hooks/use-toast';
import { errorMessage } from './errors';

// Ticket 176: the loan officer's SSN mask editor. A scan or photo the OCR
// gate could not clear is held on the server; this dialog shows its pages,
// the boxes the OCR suggested, and lets the loan officer drag more (or say
// there is no SSN). Numbers the OCR did read are shown as locked masks: the
// server burns those whatever the review says. Only coordinates go back; the
// server burns them in before Audrey reads the file. One component, mounted from the income
// calculator result row and from the contact's Borrower uploads card.

export const REASON_TEXT = {
  label_without_number: 'The page says "SSN" but the number next to it could not be read.',
  unreadable_page: 'Audrey could not read a page well enough to check it. It may be sideways, upside down or blurry.',
  turned_text: 'Part of a page is sideways or upside down, so Audrey could not check it.',
  ocr_timeout: 'Reading the page took too long.',
  ocr_error: 'The page could not be read.',
  no_tesseract: 'Scan reading is not set up on this server.',
  page_cap: 'Too many pages to check in one go.',
};

const MIN_SIDE = 0.005; // a drag smaller than this is a click, not a box

// The dialog is a fixed-height column: title, notice and the action buttons
// stay put; only the pages scroll, one under the other. Normal size shows
// nearly a whole page at once; full screen runs each page at the full width
// of the window, so the print is large enough to mask.
// transition-none: the size change is instant, so the page in view can be put back in place at once.
const DIALOG_CLASSES = 'max-w-3xl max-h-[92vh] overflow-hidden flex flex-col gap-3 transition-none';
const DIALOG_FULL_CLASSES = 'w-[98vw] max-w-[98vw] h-[96vh] max-h-[96vh] overflow-hidden flex flex-col gap-3 transition-none';

// Normal size: the tallest a page can be and still show whole, label included,
// between the notice and the buttons.
const FIT_HEIGHT = 'max(320px, calc(92vh - 340px))';

// A page counts as looked at once a fair share of it has been on screen: 30% of
// the page, or half the scroll area (a full-screen page is taller than the area).
export function pageInView(entry) {
  if (!entry.isIntersecting) return false;
  const root = entry.rootBounds?.height || 0;
  return entry.intersectionRatio >= 0.3 || (root > 0 && entry.intersectionRect.height >= root * 0.5);
}

// The pages a loan officer has to look at before a review can be saved: the
// ones the OCR could not clear, or every page when none was inspected.
export function pagesToCheck(pages) {
  const flagged = pages.filter((p) => p.flagged).map((p) => p.index);
  return flagged.length ? flagged : pages.map((p) => p.index);
}

const clamp01 = (v) => Math.min(1, Math.max(0, v));

function normalize(a, b) {
  return [Math.min(a.x, b.x), Math.min(a.y, b.y), Math.max(a.x, b.x), Math.max(a.y, b.y)];
}

export function PageEditor({ page, boxes, onChange, total = 1, full = false, pageRef, onImageLoad }) {
  const locked = page.locked || [];
  // The page's shape is known before its picture is decoded, so the layout does not jump.
  const shape = page.width > 0 && page.height > 0 ? `${page.width} / ${page.height}` : undefined;
  const ref = useRef(null);
  const [drag, setDrag] = useState(null); // {start, now}

  const point = (e) => {
    const r = ref.current.getBoundingClientRect();
    return {
      x: clamp01((e.clientX - r.left) / (r.width || 1)),
      y: clamp01((e.clientY - r.top) / (r.height || 1)),
    };
  };
  const down = (e) => {
    if (e.button !== undefined && e.button !== 0) return;
    e.preventDefault();
    const p = point(e);
    setDrag({ start: p, now: p });
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };
  const move = (e) => { if (drag) setDrag({ ...drag, now: point(e) }); };
  const up = (e) => {
    if (!drag) return;
    const box = normalize(drag.start, point(e));
    setDrag(null);
    if (box[2] - box[0] < MIN_SIDE || box[3] - box[1] < MIN_SIDE) return;
    onChange([...boxes, box]);
  };
  const remove = (i) => onChange(boxes.filter((_, j) => j !== i));
  const live = drag ? normalize(drag.start, drag.now) : null;

  const pct = ([x0, y0, x1, y1]) => ({
    left: `${x0 * 100}%`, top: `${y0 * 100}%`, width: `${(x1 - x0) * 100}%`, height: `${(y1 - y0) * 100}%`,
  });

  return (
    <div className="space-y-1" ref={pageRef} data-page={page.index} data-testid={`ssn-page-${page.index}`}>
      <div className="text-[11px] font-semibold text-slate-500 flex items-center gap-2 flex-wrap">
        <span>Page {page.index + 1}{total > 1 ? ` of ${total}` : ''}</span>
        {page.flagged && total > 1 && (
          <span className="px-1.5 py-0.5 rounded bg-amber-100 text-amber-800" data-testid={`ssn-page-flag-${page.index}`}>Audrey could not clear this page</span>
        )}
        {boxes.length > 0 && <span className="font-normal text-slate-400">{boxes.length} mask{boxes.length === 1 ? '' : 's'}</span>}
        {locked.length > 0 && <span className="font-normal text-slate-400" data-testid={`ssn-locked-count-${page.index}`}>{locked.length} masked by Audrey</span>}
      </div>
      <div ref={ref} className={`relative select-none touch-none border border-slate-200 rounded-md overflow-hidden bg-slate-50 ${full ? 'block w-full' : 'table mx-auto'}`}
        style={{ cursor: 'crosshair' }}
        onPointerDown={down} onPointerMove={move} onPointerUp={up} onPointerCancel={() => setDrag(null)}
        data-testid={`ssn-canvas-${page.index}`}>
        <img src={`data:image/jpeg;base64,${page.image}`} alt={`Page ${page.index + 1}`} draggable={false}
          onLoad={onImageLoad} className={full ? 'block w-full h-auto' : 'block w-auto max-w-full'}
          style={full ? { aspectRatio: shape } : { maxHeight: FIT_HEIGHT, aspectRatio: shape }} />
        {locked.map((b, i) => (
          <div key={`l${i}`} className="absolute bg-black/80 border-2 border-slate-500 pointer-events-none" style={pct(b)}
            title="Audrey read a number here. It is always masked." data-testid={`ssn-locked-${page.index}-${i}`} />
        ))}
        {boxes.map((b, i) => (
          <div key={i} className="absolute bg-black/80 border-2 border-amber-400" style={pct(b)} data-testid={`ssn-box-${page.index}-${i}`}>
            <button type="button" onPointerDown={(e) => e.stopPropagation()} onClick={(e) => { e.stopPropagation(); remove(i); }}
              className="absolute -top-2.5 -right-2.5 w-5 h-5 rounded-full bg-white border border-slate-300 text-slate-700 flex items-center justify-center shadow"
              aria-label={`Remove mask ${i + 1} on page ${page.index + 1}`} data-testid={`ssn-box-remove-${page.index}-${i}`}>
              <X size={10} />
            </button>
          </div>
        ))}
        {live && <div className="absolute bg-black/50 border-2 border-dashed border-amber-400 pointer-events-none" style={pct(live)} />}
      </div>
    </div>
  );
}

export default function SsnReviewDialog({ reviewId, open, onOpenChange, onReviewed, filename }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [boxes, setBoxes] = useState({});
  const [saving, setSaving] = useState(false);
  const [full, setFull] = useState(false);
  const [seen, setSeen] = useState(() => new Set());
  const [loaded, setLoaded] = useState(() => new Set()); // pages whose picture is on screen
  const scroller = useRef(null);
  const pageEls = useRef({});
  const jumped = useRef(false);
  const anchor = useRef(null);
  // Without an observer (old browser) the look-first rule cannot be checked, so it is off.
  const canWatch = typeof IntersectionObserver !== 'undefined';

  useEffect(() => {
    if (!open || !reviewId) return undefined;
    let cancelled = false;
    setData(null); setError(null); setBoxes({}); setSeen(new Set()); setLoaded(new Set()); setFull(false);
    pageEls.current = {}; jumped.current = false;
    api.get(`/income/ssn-review/${reviewId}`)
      .then(({ data: d }) => {
        if (cancelled) return;
        setData(d);
        const pre = {};
        (d.pages || []).forEach((p) => { if (p.suggested?.length) pre[p.index] = p.suggested.map((b) => [...b]); });
        setBoxes(pre);
      })
      .catch((e) => { if (!cancelled) setError(e?.response?.status === 410 ? 'This file is no longer held. Drop it again to review it.' : errorMessage(e)); });
    return () => { cancelled = true; };
  }, [open, reviewId]);

  const count = Object.values(boxes).reduce((n, b) => n + b.length, 0);
  const held = data?.status === 'held';
  const pages = useMemo(() => data?.pages || [], [data]);
  const lockedCount = pages.reduce((n, p) => n + (p.locked?.length || 0), 0);
  // Nothing can be looked at, or saved, before every page's picture is showing.
  const ready = pages.length > 0 && pages.every((p) => loaded.has(p.index));
  const unseen = canWatch ? pagesToCheck(pages).filter((i) => !seen.has(i)) : [];
  const scrollTo = (i) => pageEls.current[i]?.scrollIntoView?.({ block: 'start', behavior: 'smooth' });

  // Changing size keeps the same spot of the same page at the top of the scroll area.
  const toggleFull = () => {
    const sc = scroller.current;
    if (sc) {
      const box = sc.getBoundingClientRect();
      // The page taking up the most of the scroll area is the one being worked on.
      const hit = pages.map((p) => ({ index: p.index, r: pageEls.current[p.index]?.getBoundingClientRect() }))
        .filter((x) => x.r && x.r.height > 0)
        .map((x) => ({ ...x, shown: Math.min(x.r.bottom, box.bottom) - Math.max(x.r.top, box.top) }))
        .sort((a, b) => b.shown - a.shown)[0];
      anchor.current = hit ? { index: hit.index, frac: Math.max(0, (box.top - hit.r.top) / hit.r.height) } : null;
    }
    setFull((v) => !v);
  };
  useLayoutEffect(() => {
    const a = anchor.current; const sc = scroller.current; const el = a && pageEls.current[a.index];
    anchor.current = null;
    if (!a || !sc || !el) return;
    const r = el.getBoundingClientRect();
    sc.scrollTop += r.top - sc.getBoundingClientRect().top + a.frac * r.height;
  }, [full]);

  // A page is marked as looked at when it has been on screen in the scroll area.
  // Before the pictures show, every page is a thin strip that fits on screen at
  // once, so the watching starts only when they all have their size.
  useEffect(() => {
    if (!held || !ready || !canWatch || !scroller.current) return undefined;
    const io = new IntersectionObserver((entries) => {
      const hit = entries.filter(pageInView).map((e) => Number(e.target.dataset.page));
      if (hit.length) setSeen((prev) => (hit.every((i) => prev.has(i)) ? prev : new Set([...prev, ...hit])));
    }, { root: scroller.current, threshold: [0, 0.15, 0.3, 0.5, 0.75, 1] });
    Object.values(pageEls.current).forEach((el) => el && io.observe(el));
    return () => io.disconnect();
  }, [held, data, ready, canWatch, full]);

  // Once every page image has a size, open on the first page that needs a look.
  useEffect(() => {
    if (!held || jumped.current || !ready) return;
    jumped.current = true;
    const first = pagesToCheck(pages)[0] ?? 0;
    if (first > 0) pageEls.current[first]?.scrollIntoView?.({ block: 'start' });
  }, [held, pages, ready]);

  const submit = async (body) => {
    setSaving(true);
    try {
      const { data: out } = await api.post(`/income/ssn-review/${reviewId}`, body);
      toast({
        title: body.no_ssn ? 'Marked: no SSN on this file' : `${out.box_count} mask${out.box_count === 1 ? '' : 's'} applied`,
        description: 'Audrey can read it now.',
      });
      onOpenChange(false);
      onReviewed?.(out);
    } catch (e) {
      const status = e?.response?.status;
      if (status === 409) {
        // Someone else saved a review first. Theirs stands; the caller refreshes.
        toast({ title: 'This file was already reviewed', description: 'The first review was kept.' });
        onOpenChange(false);
        onReviewed?.({ review_id: reviewId, status: 'reviewed', already_reviewed: true });
      } else if (status === 410) {
        setData((d) => (d ? { ...d, status: 'expired', pages: [] } : d));
      } else {
        toast({ title: 'Could not save the review', description: errorMessage(e), variant: 'destructive' });
      }
    } finally { setSaving(false); }
  };

  const reasons = (data?.reasons || []).map((r) => REASON_TEXT[r] || r);

  return (
    <DialogShell
      open={open}
      onOpenChange={onOpenChange}
      size="lg"
      className={full ? DIALOG_FULL_CLASSES : DIALOG_CLASSES}
      title="Mask the SSN before Audrey reads this file"
      description={filename || data?.filename || ''}
      data-testid="ssn-review-dialog"
      footer={held && pages.length > 0 && data.watch_only ? (
        <span className="text-[12px] text-slate-600" data-testid="ssn-watch-only">Only staff on the borrower&apos;s loan can review this file.</span>
      ) : held && pages.length > 0 ? (
        <div className="flex items-center gap-2 w-full flex-wrap">
          <span className="text-[11px] text-slate-500 mr-auto" data-testid="ssn-box-count">
            {count === 0 ? 'Drag a box over each SSN.' : `${count} mask${count === 1 ? '' : 's'} to apply`}
            {lockedCount > 0 && ` · ${lockedCount} already masked by Audrey`}
            {ready && unseen.length > 0 && (
              <button type="button" onClick={() => scrollTo(unseen[0])} className="ml-2 font-semibold text-amber-700 underline" data-testid="ssn-unseen">
                Scroll to page {unseen.map((i) => i + 1).join(', ')} first.
              </button>
            )}
          </span>
          <button type="button" onClick={() => submit({ no_ssn: true, boxes: {} })} disabled={saving || !ready || unseen.length > 0}
            className="text-[12px] font-semibold px-3 py-1.5 rounded-lg border border-slate-200 hover:bg-slate-50 disabled:opacity-50"
            data-testid="ssn-no-ssn">{lockedCount > 0 ? 'No other SSN' : 'No SSN here'}</button>
          <button type="button" onClick={() => submit({ no_ssn: false, boxes })} disabled={saving || !ready || count === 0 || unseen.length > 0}
            className="text-[12px] font-semibold px-3 py-1.5 rounded-lg bg-brand-navy text-white disabled:opacity-50"
            data-testid="ssn-apply">{saving ? 'Saving…' : 'Apply masks'}</button>
        </div>
      ) : null}
    >
      {error && <p className="text-[13px] text-red-600" data-testid="ssn-review-error">{error}</p>}
      {!error && !data && <p className="text-[13px] text-slate-500">Loading the pages…</p>}
      {data && !held && (
        <p className="text-[13px] text-slate-600" data-testid="ssn-review-done">
          {data.status === 'reviewed' ? 'This file was already reviewed.' : 'This file is no longer held. Drop it again to review it.'}
        </p>
      )}
      {held && pages.length === 0 && (
        <p className="text-[13px] text-red-600" data-testid="ssn-review-no-pages">The pages of this file could not be shown. Drop it again.</p>
      )}
      {held && pages.length > 0 && (
        <>
          <div className="shrink-0 flex items-start gap-2 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-[12px] text-amber-900">
            <ShieldAlert size={14} className="shrink-0 mt-0.5" />
            <div>
              <div className="font-semibold">Audrey held this file.</div>
              {reasons.map((r, i) => <div key={i}>{r}</div>)}
              <div className="mt-1 text-amber-800">Drag over every Social Security number. Suggested boxes are already drawn; remove any that are wrong.{lockedCount > 0 ? ' Numbers Audrey read herself are masked already and stay masked.' : ''} The masked copy is what Audrey reads; the original stays as uploaded.</div>
            </div>
          </div>
          <div className="shrink-0 flex items-center gap-2 text-[11px] text-slate-500">
            {pages.length > 1 && <span className="font-semibold" data-testid="ssn-page-total">{pages.length} pages · scroll to see each one</span>}
            <button type="button" onClick={toggleFull} aria-pressed={full}
              className="ml-auto font-semibold px-2.5 py-1 rounded-lg border border-slate-200 text-slate-700 hover:bg-slate-50 inline-flex items-center gap-1.5"
              data-testid="ssn-full-toggle">
              {full ? <Minimize2 size={12} /> : <Maximize2 size={12} />}
              {full ? 'Exit full screen' : 'Full screen'}
            </button>
          </div>
          <div ref={scroller} className="flex-1 min-h-0 overflow-y-auto pr-1 space-y-4 [overflow-anchor:none]" data-testid="ssn-pages">
            {pages.map((p) => (
              <PageEditor key={p.index} page={p} total={pages.length} full={full} boxes={boxes[p.index] || []}
                pageRef={(el) => { pageEls.current[p.index] = el; }}
                onImageLoad={() => setLoaded((prev) => (prev.has(p.index) ? prev : new Set([...prev, p.index])))}
                onChange={(next) => setBoxes((prev) => ({ ...prev, [p.index]: next }))} />
            ))}
          </div>
        </>
      )}
    </DialogShell>
  );
}
